import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { entregarArchivo } from "../../../../utils/entregarArchivo";

import { RootState } from "../../../../globals";
import useEsTelefono from "../../../../utils/esTelefono";
import { ScheduleBalancesService } from "../../../../services/v2/scheduleBalances.service";
import "./scheduleBalances.css";
import { BotonWhatsappAlumno } from "../../../../components/whatsapp/whatsappAlumno";
import EnvioMasivo from "../../../../components/whatsapp/envioMasivo";
import horarioBonito from "../../../../utils/horarioBonito";

const servicio = new ScheduleBalancesService();

/*
	Saldos por Horario (Finanzas, Esteban 2026-10-02): el "reporte de saldos" del Analytics viejo.
	Cuánto se ha cobrado de cada horario, cuánto está vencido y cuánto falta, con sus alumnos.
	Los filtros son de selección múltiple, como en el Catálogo de Horarios; el rango de fechas
	cambia "Cobrado" a lo cobrado en esas fechas (por fecha del pago).
*/

type Horario = {
	id: number; plantel: number; curso: string; modulo: string; periodo: string; periodoId: number; periodoInicio: string;
	horario: string; nombre: string; maestro: string; inicio: string; fin: string; estado: string; status: number; meses: number;
	mensualidad: number; activos: number; matriculas: number; retirados: number; graduados: number;
	cobradoMens: number; cobradoInsc: number; cobradoRec: number; cobradoKits: number; cobradoProd: number; cobradoServ: number;
	vencido: number; porCobrar: number;
	cuotas: number; cuotasPagadas: number; alumnosVencidos: number;
};
type Plantel = { id: number; nombre: string; moneda: string };
type Alumno = {
	matricula: number; alumnoId: number; correlativo: string; nombre: string; estado: string; activa: boolean;
	cobradoMens: number; cobradoInsc: number; cobradoRec: number; cobradoKits: number; cobradoProd: number; cobradoServ: number; ultimoPago: string | null;
	vencido: number; vencidoDesde: string | null; porCobrar: number; cuotas: number; cuotasPagadas: number;
};
type Dinero = { [moneda: string]: number };
type Agrupar = "curso" | "periodo" | "plantel" | "ninguno";
type Columna = "horario" | "alumnos" | "mens" | "otros" | "kits" | "prod" | "serv" | "cobrado" | "vencido" | "porCobrar" | "avance";

// Status del horario en WAPP. Arranca en Activo (Esteban, 2026-10-02: fuera "Nunca comenzó" y demás).
const STATUS: { id: string; nombre: string }[] = [
	{ id: "1", nombre: "Activo" },
	{ id: "0", nombre: "Nunca comenzó" },
	{ id: "2", nombre: "Fusionado" },
	{ id: "3", nombre: "Completado" }
];
const STATUS_INICIAL = ["1"];
// Y que no hayan terminado: la misma regla del Catálogo de Horarios (muchos Activos viejos nunca se cerraron).
const FECHAS_INICIAL = ["en-curso", "por-comenzar"];
const ESTADOS: { id: string; nombre: string }[] = [
	{ id: "en-curso", nombre: "En curso" },
	{ id: "por-comenzar", nombre: "Por comenzar" },
	{ id: "terminado", nombre: "Terminados" }
];
const SIMBOLO: { [moneda: string]: string } = { MXN: "$", HNL: "L " };
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// ------------------------------------------------------------------ formato

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fechaCorta = (valor: string | null, conAnio = true): string => {
	if (!valor) {
		return "–";
	}
	const [a, m, d] = valor.substring(0, 10).split("-").map(Number);
	return `${d} ${MESES[m - 1]}` + (conAnio ? ` ${a}` : "");
};

const cifra = (n: number, decimales = 0) => n.toLocaleString("en-US", { minimumFractionDigits: decimales, maximumFractionDigits: decimales });

const dineroTexto = (moneda: string, n: number, decimales = 0) => `${SIMBOLO[moneda] ?? ""}${cifra(n, decimales)}`;

const sumar = (a: Dinero, moneda: string, n: number) => { a[moneda] = (a[moneda] ?? 0) + n; };

type Cobros = { cobradoMens: number; cobradoInsc: number; cobradoRec: number; cobradoKits: number; cobradoProd: number; cobradoServ: number };
/** Lo cobrado de la colegiatura del horario: mensualidades, inscripción y recargos. Es lo que mide el avance. */
const colegiaturaDe = (h: Cobros) => h.cobradoMens + h.cobradoInsc + h.cobradoRec;
/** Todo lo que pagaron: colegiatura + kits, productos y servicios mientras estuvieron en el horario. */
const cobradoDe = (h: Cobros) => colegiaturaDe(h) + h.cobradoKits + h.cobradoProd + h.cobradoServ;

const sinAcentos = (t: string) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Una cifra por moneda, una debajo de otra (MTY/TRC en pesos, Honduras en lempiras). */
const Montos: React.FC<{ valor: Dinero; clase?: string }> = ({ valor, clase }) => {
	const monedas = Object.keys(valor).filter((m) => Math.abs(valor[m]) >= 0.5 || Object.keys(valor).length === 1);
	if (monedas.length === 0) {
		return <span className={clase}>{dineroTexto(Object.keys(valor)[0] ?? "MXN", 0)}</span>;
	}
	return (
		<React.Fragment>
			{monedas.map((m) => <span key={m} className={"wshMonto " + (clase ?? "")}>{dineroTexto(m, valor[m])}</span>)}
		</React.Fragment>
	);
};

/** Barra: cobrado / vencido / por cobrar, sobre el total del horario. */
const Avance: React.FC<{ cobrado: number; vencido: number; porCobrar: number }> = ({ cobrado, vencido, porCobrar }) => {
	const total = cobrado + vencido + porCobrar;
	if (total <= 0) {
		return <span className="wshTenue">–</span>;
	}
	const pc = (n: number) => `${(n / total) * 100}%`;
	return (
		<div className="wshAvance" title={`Cobrado ${Math.round((cobrado / total) * 100)}% · Vencido ${Math.round((vencido / total) * 100)}% · Por cobrar ${Math.round((porCobrar / total) * 100)}%`}>
			<div className="wshBarra">
				<span className="wshBarraCobrado" style={{ width: pc(cobrado) }} />
				<span className="wshBarraVencido" style={{ width: pc(vencido) }} />
			</div>
			<span className="wshAvancePct">{Math.round((cobrado / total) * 100)}%</span>
		</div>
	);
};

// ------------------------------------------------------------------ filtro de selección múltiple

const Desplegable: React.FC<{
	titulo: string; opciones: { id: string; nombre: string; extra?: string }[];
	elegidos: Set<string>; cambiar: (s: Set<string>) => void; ancho?: number;
}> = ({ titulo, opciones, elegidos, cambiar, ancho }) => {
	const [abierto, setAbierto] = useState(false);
	const [buscar, setBuscar] = useState("");
	const caja = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!abierto) {
			return;
		}
		const cerrar = (e: MouseEvent) => {
			if (caja.current && !caja.current.contains(e.target as Node)) {
				setAbierto(false);
			}
		};
		document.addEventListener("mousedown", cerrar);
		return () => document.removeEventListener("mousedown", cerrar);
	}, [abierto]);

	const visibles = opciones.filter((o) => buscar === "" || sinAcentos(o.nombre + " " + (o.extra ?? "")).includes(sinAcentos(buscar)));
	const activos = Array.from(elegidos).filter((id) => opciones.some((o) => o.id === id)).length;

	return (
		<div className="wshDrop" ref={caja}>
			<button type="button" className={"wshDropBtn" + (elegidos.size > 0 ? " wshDropBtnOn" : "")} onClick={() => setAbierto(!abierto)}>
				{titulo}{elegidos.size > 0 ? ` · ${activos || elegidos.size}` : ""}
				<span className="wshChevron">▾</span>
			</button>
			{abierto && (
				<div className="wshDropPanel" style={ancho ? { width: ancho } : undefined}>
					{opciones.length > 8 && (
						<input className="wshDropFind" placeholder="Buscar" value={buscar} autoFocus onChange={(e) => setBuscar(e.target.value)} />
					)}
					<div className="wshDropList">
						{visibles.map((o) => (
							<div key={o.id} className="wshDropItem" onClick={() => {
								const s = new Set(elegidos);
								s.has(o.id) ? s.delete(o.id) : s.add(o.id);
								cambiar(s);
							}}>
								<input type="checkbox" readOnly checked={elegidos.has(o.id)} />
								<span className="wshDropName">{o.nombre}</span>
								{o.extra && <span className="wshDropExtra">{o.extra}</span>}
							</div>
						))}
						{visibles.length === 0 && <div className="wshDropEmpty">Sin opciones</div>}
					</div>
					{elegidos.size > 0 && (
						<button type="button" className="wshDropClear" onClick={() => cambiar(new Set())}>Limpiar</button>
					)}
				</div>
			)}
		</div>
	);
};

// ------------------------------------------------------------------ pantalla

const ScheduleBalancesController: React.FC = () => {
	const movil = useEsTelefono();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;

	const [datos, setDatos] = useState<{ planteles: Plantel[]; horarios: Horario[]; generado: string; hoy: string } | null>(null);
	// WhatsApp a los que deben de un horario (Esteban, 2026-10-18): la plantilla de saldo vencido les llega con su monto y meses.
	const [waEnvio, setWaEnvio] = useState<{ ids: number[]; plantel: number; ref: string } | null>(null);
	const [cargando, setCargando] = useState(false);
	const [error, setError] = useState("");

	const [desde, setDesde] = useState("");
	const [hasta, setHasta] = useState("");
	const [planteles, setPlanteles] = useState<Set<string>>(new Set());
	const [cursos, setCursos] = useState<Set<string>>(new Set());
	const [periodos, setPeriodos] = useState<Set<string>>(new Set());
	const [horarios, setHorarios] = useState<Set<string>>(new Set());
	const [maestros, setMaestros] = useState<Set<string>>(new Set());
	const [estados, setEstados] = useState<Set<string>>(new Set(FECHAS_INICIAL));
	const [statuses, setStatuses] = useState<Set<string>>(new Set(STATUS_INICIAL));
	const [buscar, setBuscar] = useState("");
	const [soloVencido, setSoloVencido] = useState(false);
	const [agrupar, setAgrupar] = useState<Agrupar>("curso");
	const [orden, setOrden] = useState<{ col: Columna; dir: 1 | -1 }>({ col: "horario", dir: 1 });
	const [abiertos, setAbiertos] = useState<Set<number>>(new Set());
	const [alumnos, setAlumnos] = useState<{ [id: number]: Alumno[] | "cargando" | "error" }>({});
	// Plantel de la barra con el que se armaron los filtros: si cambia, los filtros vuelven a empezar.
	const plantelDeFiltros = useRef<number | null>(null);
	const fresco = useRef(false);
	const [recargar, setRecargar] = useState(0);

	// Cargar (y recargar al cambiar el rango de fechas).
	useEffect(() => {
		if (campusId === null) {
			return;
		}
		let vigente = true;
		setCargando(true);
		setError("");
		servicio.summary(campusId, desde, hasta, fresco.current)
			.then((r) => {
				if (!vigente) { return; }
				setDatos(r.data);
				setAlumnos({});
				if (plantelDeFiltros.current !== campusId) {
					plantelDeFiltros.current = campusId;
					const tiene = (r.data.planteles as Plantel[]).some((p) => p.id === campusId);
					setPlanteles(tiene ? new Set([String(campusId)]) : new Set());
					setCursos(new Set()); setPeriodos(new Set()); setHorarios(new Set()); setMaestros(new Set());
					setAbiertos(new Set());
				}
			})
			.catch((e) => { if (vigente) { setError(e?.response?.data?.message ?? "No se pudieron cargar los saldos."); } })
			.finally(() => { if (vigente) { setCargando(false); } });
		fresco.current = false;
		return () => { vigente = false; };
	}, [campusId, desde, hasta, recargar]);

	const monedaDe = useMemo(() => {
		const m: { [id: number]: string } = {};
		(datos?.planteles ?? []).forEach((p) => { m[p.id] = p.moneda; });
		return m;
	}, [datos]);
	const nombrePlantel = useMemo(() => {
		const m: { [id: number]: string } = {};
		(datos?.planteles ?? []).forEach((p) => { m[p.id] = p.nombre; });
		return m;
	}, [datos]);

	// Cada filtro ofrece solo lo que queda con los demás filtros puestos.
	const pasa = (h: Horario, salvo: string) =>
		(salvo === "plantel" || planteles.size === 0 || planteles.has(String(h.plantel))) &&
		(salvo === "curso" || cursos.size === 0 || cursos.has(h.curso)) &&
		(salvo === "periodo" || periodos.size === 0 || periodos.has(h.periodo)) &&
		(salvo === "horario" || horarios.size === 0 || horarios.has(String(h.id))) &&
		(salvo === "maestro" || maestros.size === 0 || maestros.has(h.maestro)) &&
		(salvo === "estado" || estados.size === 0 || estados.has(h.estado)) &&
		(salvo === "status" || statuses.size === 0 || statuses.has(String(h.status)));

	const todos = datos?.horarios ?? [];
	const opciones = useMemo(() => {
		const elegidosDe: { [salvo: string]: Set<string> } = { plantel: planteles, curso: cursos, periodo: periodos, horario: horarios, maestro: maestros };
		const unicos = (salvo: string, clave: (h: Horario) => string, nombre: (h: Horario) => string, extra?: (h: Horario) => string, ordenar?: (h: Horario) => string) => {
			const m = new Map<string, { id: string; nombre: string; extra?: string; o: string }>();
			todos.filter((h) => pasa(h, salvo)).forEach((h) => {
				const k = clave(h);
				if (!m.has(k)) {
					m.set(k, { id: k, nombre: nombre(h), extra: extra?.(h), o: ordenar ? ordenar(h) : sinAcentos(nombre(h)) });
				}
			});
			// Lo elegido que ya no tiene horarios con los demás filtros sigue en la lista, para poder quitarlo.
			elegidosDe[salvo]?.forEach((k) => {
				const h = m.has(k) ? null : todos.find((x) => clave(x) === k);
				if (h) {
					m.set(k, { id: k, nombre: nombre(h), extra: "0 horarios", o: ordenar ? ordenar(h) : sinAcentos(nombre(h)) });
				}
			});
			return Array.from(m.values()).sort((a, b) => a.o < b.o ? -1 : a.o > b.o ? 1 : 0);
		};
		return {
			planteles: unicos("plantel", (h) => String(h.plantel), (h) => nombrePlantel[h.plantel] ?? String(h.plantel)),
			cursos: unicos("curso", (h) => h.curso, (h) => h.curso),
			// Períodos del más reciente al más viejo.
			periodos: unicos("periodo", (h) => h.periodo, (h) => h.periodo, undefined, (h) => String(99999999 - Number(h.periodoInicio.replace(/-/g, "")))),
			horarios: unicos("horario", (h) => String(h.id), (h) => `${h.curso} · ${horarioBonito(h.horario)}`, (h) => h.periodo + (planteles.size !== 1 ? " · " + (nombrePlantel[h.plantel] ?? "") : "")),
			maestros: unicos("maestro", (h) => h.maestro, (h) => h.maestro || "Sin maestro")
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [todos, planteles, cursos, periodos, horarios, maestros, estados, statuses, nombrePlantel]);

	const filtrados = useMemo(() => {
		const q = sinAcentos(buscar.trim());
		return todos.filter((h) => pasa(h, "") &&
			(!soloVencido || h.vencido > 0.5) &&
			(q === "" || sinAcentos(`${h.curso} ${h.modulo} ${h.horario} ${h.nombre} ${h.periodo} ${h.maestro}`).includes(q)));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [todos, planteles, cursos, periodos, horarios, maestros, estados, statuses, buscar, soloVencido]);

	const varios = new Set(filtrados.map((h) => h.plantel)).size > 1;

	// Totales de lo que se ve.
	const totales = useMemo(() => {
		const t = { cobrado: {} as Dinero, mens: {} as Dinero, otros: {} as Dinero, kits: {} as Dinero, prodServ: {} as Dinero, vencido: {} as Dinero, porCobrar: {} as Dinero, activos: 0, conVencido: 0, matriculas: 0 };
		filtrados.forEach((h) => {
			const m = monedaDe[h.plantel] ?? "MXN";
			sumar(t.cobrado, m, cobradoDe(h));
			sumar(t.mens, m, h.cobradoMens);
			sumar(t.otros, m, h.cobradoInsc + h.cobradoRec);
			sumar(t.kits, m, h.cobradoKits);
			sumar(t.prodServ, m, h.cobradoProd + h.cobradoServ);
			sumar(t.vencido, m, h.vencido);
			sumar(t.porCobrar, m, h.porCobrar);
			t.activos += h.activos;
			t.matriculas += h.matriculas;
			t.conVencido += h.alumnosVencidos;
		});
		return t;
	}, [filtrados, monedaDe]);

	// Orden y grupos.
	const valorOrden = (h: Horario): number | string => {
		switch (orden.col) {
			case "alumnos": return h.activos;
			case "mens": return h.cobradoMens;
			case "otros": return h.cobradoInsc + h.cobradoRec;
			case "kits": return h.cobradoKits;
			case "prod": return h.cobradoProd;
			case "serv": return h.cobradoServ;
			case "cobrado": return cobradoDe(h);
			case "vencido": return h.vencido;
			case "porCobrar": return h.porCobrar;
			case "avance": {
				const total = colegiaturaDe(h) + h.vencido + h.porCobrar;
				return total > 0 ? colegiaturaDe(h) / total : -1;
			}
			default: return "";
		}
	};
	const grupos = useMemo(() => {
		const filas = [...filtrados].sort((a, b) => {
			if (orden.col !== "horario") {
				const va = valorOrden(a) as number, vb = valorOrden(b) as number;
				if (va !== vb) { return (va - vb) * orden.dir; }
			}
			// Por defecto: el más reciente arriba, y a igual inicio por hora del día.
			if (a.inicio !== b.inicio) { return a.inicio < b.inicio ? orden.dir : -orden.dir; }
			return a.horario < b.horario ? -1 : 1;
		});
		if (agrupar === "ninguno") {
			return [{ clave: "", titulo: "", filas }];
		}
		const claveDe = (h: Horario) => agrupar === "curso" ? h.curso : agrupar === "periodo" ? h.periodo : nombrePlantel[h.plantel] ?? "";
		const m = new Map<string, Horario[]>();
		filas.forEach((h) => {
			const k = claveDe(h);
			if (!m.has(k)) { m.set(k, []); }
			m.get(k)!.push(h);
		});
		const lista = Array.from(m.entries()).map(([clave, f]) => ({ clave, titulo: clave, filas: f }));
		if (agrupar === "periodo") {
			lista.sort((a, b) => a.filas[0].periodoInicio < b.filas[0].periodoInicio ? 1 : -1);
		} else {
			lista.sort((a, b) => sinAcentos(a.clave) < sinAcentos(b.clave) ? -1 : 1);
		}
		return lista;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filtrados, agrupar, orden, nombrePlantel]);

	const subtotal = (filas: Horario[]) => {
		const s = { mens: {} as Dinero, otros: {} as Dinero, kits: {} as Dinero, prod: {} as Dinero, serv: {} as Dinero, cobrado: {} as Dinero, vencido: {} as Dinero, porCobrar: {} as Dinero, activos: 0, matriculas: 0, conVencido: 0, c: 0, v: 0, p: 0 };
		filas.forEach((h) => {
			const m = monedaDe[h.plantel] ?? "MXN";
			sumar(s.mens, m, h.cobradoMens);
			sumar(s.otros, m, h.cobradoInsc + h.cobradoRec);
			sumar(s.kits, m, h.cobradoKits);
			sumar(s.prod, m, h.cobradoProd);
			sumar(s.serv, m, h.cobradoServ);
			sumar(s.cobrado, m, cobradoDe(h));
			sumar(s.vencido, m, h.vencido);
			sumar(s.porCobrar, m, h.porCobrar);
			s.activos += h.activos; s.matriculas += h.matriculas; s.conVencido += h.alumnosVencidos;
			s.c += colegiaturaDe(h); s.v += h.vencido; s.p += h.porCobrar;
		});
		return s;
	};

	const abrir = (h: Horario) => {
		const s = new Set(abiertos);
		if (s.has(h.id)) {
			s.delete(h.id);
			setAbiertos(s);
			return;
		}
		s.add(h.id);
		setAbiertos(s);
		if (alumnos[h.id] && alumnos[h.id] !== "error") {
			return;
		}
		setAlumnos((a) => ({ ...a, [h.id]: "cargando" }));
		servicio.students(campusId as number, h.id, desde, hasta)
			.then((r) => setAlumnos((a) => ({ ...a, [h.id]: r.data.alumnos })))
			.catch(() => setAlumnos((a) => ({ ...a, [h.id]: "error" })));
	};

	const ordenarPor = (col: Columna) => {
		if (orden.col === col) {
			setOrden({ col, dir: orden.dir === 1 ? -1 : 1 });
		} else {
			setOrden({ col, dir: col === "horario" ? 1 : -1 });
		}
	};

	// Atajos de fechas.
	const hoy = new Date();
	const atajos: { id: string; nombre: string; d: string; h: string }[] = [
		{ id: "todo", nombre: "Todo", d: "", h: "" },
		{ id: "mes", nombre: "Este mes", d: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), h: iso(hoy) },
		{ id: "pasado", nombre: "Mes pasado", d: iso(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)), h: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 0)) },
		{ id: "30", nombre: "Últimos 30 días", d: iso(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 29)), h: iso(hoy) },
		{ id: "anio", nombre: "Este año", d: iso(new Date(hoy.getFullYear(), 0, 1)), h: iso(hoy) }
	];
	const conRango = desde !== "" || hasta !== "";
	const etiquetaCobrado = conRango
		? `Cobrado ${desde ? fechaCorta(desde, false) : "…"} – ${hasta ? fechaCorta(hasta) : "hoy"}`
		: "Cobrado";

	// "Quitar filtros" regresa a como abre la pantalla: el plantel de la barra, Activos, en curso o por comenzar.
	const plantelInicial = campusId !== null && (datos?.planteles ?? []).some((p) => p.id === campusId) ? [String(campusId)] : [];
	const igual = (a: Set<string>, b: string[]) => a.size === b.length && b.every((x) => a.has(x));
	const hayFiltros = cursos.size + periodos.size + horarios.size + maestros.size > 0 || buscar !== "" || soloVencido ||
		!igual(planteles, plantelInicial) || !igual(statuses, STATUS_INICIAL) || !igual(estados, FECHAS_INICIAL);
	const limpiar = () => {
		setPlanteles(new Set(plantelInicial)); setCursos(new Set()); setPeriodos(new Set()); setHorarios(new Set()); setMaestros(new Set());
		setEstados(new Set(FECHAS_INICIAL)); setStatuses(new Set(STATUS_INICIAL)); setBuscar(""); setSoloVencido(false);
	};

	// Descargas (CSV que abre Excel).
	const csv = (filas: (string | number)[][], archivo: string) => {
		const texto = filas.map((f) => f.map((c) => {
			const s = String(c ?? "");
			return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
		}).join(",")).join("\r\n");
		entregarArchivo(new Blob(["﻿" + texto], { type: "text/csv;charset=utf-8" }), archivo, "Saldos por horario");
	};
	const sufijo = conRango ? `_${desde || "inicio"}_a_${hasta || "hoy"}` : "";
	const descargarHorarios = () => {
		const encabezado = ["Plantel", "Moneda", "Curso", "Módulo", "Periodo", "Horario", "Nombre en WAPP", "Maestro", "Inicio", "Fin", "Status", "Fechas",
			"Alumnos activos", "Matrículas", "Retirados", "Graduados", "Mensualidades", "Inscripción", "Recargos",
			"Kits", "Productos", "Servicios", conRango ? etiquetaCobrado : "Cobrado total", "Vencido", "Alumnos con vencido", "Por cobrar", "Mensualidades pagadas", "Mensualidades generadas"];
		const filas = grupos.flatMap((g) => g.filas).map((h) => [
			nombrePlantel[h.plantel] ?? "", monedaDe[h.plantel] ?? "", h.curso, h.modulo, h.periodo, h.horario, h.nombre, h.maestro, h.inicio, h.fin,
			STATUS.find((e) => e.id === String(h.status))?.nombre ?? String(h.status), ESTADOS.find((e) => e.id === h.estado)?.nombre ?? h.estado, h.activos, h.matriculas, h.retirados, h.graduados,
			h.cobradoMens, h.cobradoInsc, h.cobradoRec, h.cobradoKits, h.cobradoProd, h.cobradoServ, cobradoDe(h), h.vencido, h.alumnosVencidos, h.porCobrar, h.cuotasPagadas, h.cuotas
		]);
		csv([encabezado, ...filas], `Saldos_por_horario${sufijo}.csv`);
	};
	const descargarAlumnos = (h: Horario, lista: Alumno[]) => {
		const encabezado = ["Alumno", "Correlativo", "Matrícula", "Meses pagados", "Meses", "Mensualidades", "Inscripción",
			"Recargos", "Kits", "Productos", "Servicios", "Total cobrado", "Vencido", "Vencido desde", "Por cobrar", "Último pago"];
		const filas = lista.map((a) => [a.nombre, a.correlativo, a.estado, a.cuotasPagadas, a.cuotas, a.cobradoMens, a.cobradoInsc, a.cobradoRec,
			a.cobradoKits, a.cobradoProd, a.cobradoServ, cobradoDe(a), a.vencido, a.vencidoDesde ?? "", a.porCobrar, a.ultimoPago ?? ""]);
		csv([[`${h.curso} · ${h.horario} · ${h.periodo}`], encabezado, ...filas], `Saldos_${h.curso}_${h.periodo}_${h.id}${sufijo}.csv`.replace(/\s+/g, "_"));
	};

	if (campusId === null) {
		return <React.Fragment />;
	}

	// El pie separa productos de servicios (las tarjetas los juntan).
	const pieProd = subtotal(filtrados);

	const flecha = (col: Columna) => orden.col === col ? (orden.dir === 1 ? " ↑" : " ↓") : "";
	const columnas = varios ? 13 : 12;
	const lead = varios ? 3 : 2;
	const th = (col: Columna, texto: string) => (
		<th className="wshNum wshOrdenable" onClick={() => ordenarPor(col)}>{texto}{flecha(col)}</th>
	);
	/** Celda de dinero de un renglón: guion cuando es cero. */
	const celda = (moneda: string, n: number, clase = "", decimales = 0) => (
		<td className={"wshNum " + clase}>{n > 0.5 ? dineroTexto(moneda, n, decimales) : <span className="wshTenue">–</span>}</td>
	);


	return (
		<div className="container-fluid waModulo waSaldosHorario" style={{ padding: movil ? "10px 12px 24px 12px" : "10px 24px 32px 24px" }}>
			<div className="wshCabeza">
				<h4 className="wshTitulo">Saldos por Horario</h4>
				{datos && <span className="wshGenerado">{datos.generado}</span>}
				<button type="button" className="wshLimpiar" disabled={cargando}
					onClick={() => { fresco.current = true; setRecargar((n) => n + 1); }}>{cargando ? "Cargando…" : "Actualizar"}</button>
			</div>

			{/* Rango de fechas */}
			<div className="wshRango">
				<div className="wshAtajos">
					{atajos.map((a) => (
						<button key={a.id} type="button" className={"wshChip" + (desde === a.d && hasta === a.h ? " wshChipOn" : "")}
							onClick={() => { setDesde(a.d); setHasta(a.h); }}>{a.nombre}</button>
					))}
				</div>
				<div className="wshFechas">
					<span className="wshFechasTexto">Cobrado entre</span>
					<input type="date" className="wshFecha" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} />
					<span className="wshFechasTexto">y</span>
					<input type="date" className="wshFecha" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} />
				</div>
			</div>

			{/* Filtros */}
			<div className="wshFiltros">
				<input className="wshBuscar" placeholder="Buscar curso, horario o maestro" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
				{(datos?.planteles.length ?? 0) > 1 && (
					<Desplegable titulo="Plantel" opciones={opciones.planteles} elegidos={planteles} cambiar={setPlanteles} />
				)}
				<Desplegable titulo="Curso" opciones={opciones.cursos} elegidos={cursos} cambiar={setCursos} ancho={280} />
				<Desplegable titulo="Periodo" opciones={opciones.periodos} elegidos={periodos} cambiar={setPeriodos} />
				<Desplegable titulo="Horario" opciones={opciones.horarios} elegidos={horarios} cambiar={setHorarios} ancho={380} />
				<Desplegable titulo="Maestro" opciones={opciones.maestros} elegidos={maestros} cambiar={setMaestros} ancho={280} />
				<Desplegable titulo="Status" opciones={STATUS} elegidos={statuses} cambiar={setStatuses} />
				<Desplegable titulo="Fechas" opciones={ESTADOS} elegidos={estados} cambiar={setEstados} />
				<button type="button" className={"wshChip" + (soloVencido ? " wshChipOro" : "")} onClick={() => setSoloVencido(!soloVencido)}>Con vencido</button>
				{hayFiltros && <button type="button" className="wshLimpiar" onClick={limpiar}>Quitar filtros</button>}
			</div>

			{error && <div className="wshAviso">{error}</div>}

			{/* Tarjetas */}
			<div className={"wshTarjetas" + (cargando ? " wshCargando" : "")}>
				<div className="wshTarjeta wshTarjetaAncla">
					<span className="wshTarjetaEtiqueta">{etiquetaCobrado}</span>
					<span className="wshTarjetaCifra"><Montos valor={totales.cobrado} /></span>
				</div>
				<div className="wshTarjeta">
					<span className="wshTarjetaEtiqueta">Mensualidades</span>
					<span className="wshTarjetaCifra"><Montos valor={totales.mens} /></span>
				</div>
				<div className="wshTarjeta">
					<span className="wshTarjetaEtiqueta">Kits</span>
					<span className="wshTarjetaCifra"><Montos valor={totales.kits} /></span>
				</div>
				<div className="wshTarjeta">
					<span className="wshTarjetaEtiqueta">Productos y servicios</span>
					<span className="wshTarjetaCifra"><Montos valor={totales.prodServ} /></span>
				</div>
				<div className="wshTarjeta">
					<span className="wshTarjetaEtiqueta">Inscripción y recargos</span>
					<span className="wshTarjetaCifra"><Montos valor={totales.otros} /></span>
				</div>
				<div className="wshTarjeta">
					<span className="wshTarjetaEtiqueta">Vencido</span>
					<span className="wshTarjetaCifra wshRojo"><Montos valor={totales.vencido} /></span>
				</div>
				<div className="wshTarjeta">
					<span className="wshTarjetaEtiqueta">Por cobrar</span>
					<span className="wshTarjetaCifra"><Montos valor={totales.porCobrar} /></span>
				</div>
				<div className="wshTarjeta">
					<span className="wshTarjetaEtiqueta">Con vencido</span>
					<span className="wshTarjetaCifra wshRojo">{cifra(totales.conVencido)}<span className="wshDe"> de {cifra(totales.activos)} activos</span></span>
				</div>
			</div>

			{/* Tabla */}
			<div className="wshBarraTabla">
				<span className="wshConteo">{cifra(filtrados.length)} {filtrados.length === 1 ? "horario" : "horarios"}</span>
				<div className="wshAgrupar">
					<span className="wshFechasTexto">Agrupar por</span>
					{(["curso", "periodo", "plantel", "ninguno"] as Agrupar[]).filter((a) => a !== "plantel" || varios).map((a) => (
						<button key={a} type="button" className={"wshChip" + (agrupar === a ? " wshChipOn" : "")} onClick={() => setAgrupar(a)}>
							{a === "curso" ? "Curso" : a === "periodo" ? "Periodo" : a === "plantel" ? "Plantel" : "Nada"}
						</button>
					))}
				</div>
				<button type="button" className="wshBoton" onClick={descargarHorarios} disabled={filtrados.length === 0}>Descargar</button>
			</div>

			<div className="wshHoja">
				<div className="wshTablaCaja">
					<table className="wshTabla">
						<thead>
							<tr>
								<th className="wshOrdenable" onClick={() => ordenarPor("horario")}>Horario{flecha("horario")}</th>
								{varios && <th>Plantel</th>}
								<th>Periodo</th>
								{th("alumnos", "Alumnos")}
								{th("mens", "Mensualidades")}
								{th("otros", "Insc. y recargos")}
								{th("kits", "Kits")}
								{th("prod", "Productos")}
								{th("serv", "Servicios")}
								{th("cobrado", conRango ? "Total en rango" : "Total cobrado")}
								{th("vencido", "Vencido")}
								{th("porCobrar", "Por cobrar")}
								<th className="wshOrdenable" onClick={() => ordenarPor("avance")}>Avance{flecha("avance")}</th>
							</tr>
						</thead>
						<tbody>
							{!cargando && filtrados.length === 0 && datos && (
								<tr><td colSpan={columnas} className="wshVacio">Ningún horario con estos filtros</td></tr>
							)}
							{grupos.map((g) => {
								const st = g.titulo ? subtotal(g.filas) : null;
								return (
									<React.Fragment key={g.clave || "todos"}>
										{st && (
											<tr className="wshGrupo">
												<td colSpan={lead}>
													<span className="wshGrupoNombre">{g.titulo}</span>
													<span className="wshGrupoConteo">{g.filas.length} {g.filas.length === 1 ? "horario" : "horarios"}</span>
												</td>
												<td className="wshNum">{cifra(st.activos)}</td>
												<td className="wshNum"><Montos valor={st.mens} /></td>
												<td className="wshNum"><Montos valor={st.otros} /></td>
												<td className="wshNum"><Montos valor={st.kits} /></td>
												<td className="wshNum"><Montos valor={st.prod} /></td>
												<td className="wshNum"><Montos valor={st.serv} /></td>
												<td className="wshNum wshTotal"><Montos valor={st.cobrado} /></td>
												<td className="wshNum"><Montos valor={st.vencido} clase="wshRojo" /></td>
												<td className="wshNum"><Montos valor={st.porCobrar} /></td>
												<td>{Object.keys(st.cobrado).length === 1 && <Avance cobrado={st.c} vencido={st.v} porCobrar={st.p} />}</td>
											</tr>
										)}
										{g.filas.map((h) => {
											const m = monedaDe[h.plantel] ?? "MXN";
											const abierto = abiertos.has(h.id);
											const lista = alumnos[h.id];
											return (
												<React.Fragment key={h.id}>
													<tr className={"wshFila" + (abierto ? " wshFilaAbierta" : "")} onClick={() => abrir(h)}>
														<td className="wshCeldaHorario">
															<span className="wshAbrir">{abierto ? "▾" : "▸"}</span>
															<div className="wshHorarioTexto">
																{agrupar !== "curso" && <span className="wshCurso">{h.curso}</span>}
																<span className={agrupar === "curso" ? "wshCurso" : "wshHorarioLinea"}>{horarioBonito(h.horario)}</span>
																<span className="wshMaestro">{h.maestro || "Sin maestro"}{h.modulo && h.modulo !== h.curso ? " · " + h.modulo : ""}</span>
															</div>
														</td>
														{varios && <td className="wshNowrap">{nombrePlantel[h.plantel]}</td>}
														<td className="wshNowrap">
															{h.periodo}
															<span className="wshSub">{fechaCorta(h.inicio, false)} – {fechaCorta(h.fin)}</span>
														</td>
														<td className="wshNum">
															{cifra(h.activos)}
															{h.matriculas !== h.activos && <span className="wshDe"> / {h.matriculas}</span>}
														</td>
														{celda(m, h.cobradoMens)}
														{celda(m, h.cobradoInsc + h.cobradoRec)}
														{celda(m, h.cobradoKits)}
														{celda(m, h.cobradoProd)}
														{celda(m, h.cobradoServ)}
														{celda(m, cobradoDe(h), "wshTotal")}
														<td className="wshNum">
															{h.vencido > 0.5 ? <span className="wshRojo">{dineroTexto(m, h.vencido)}</span> : <span className="wshTenue">–</span>}
															{h.alumnosVencidos > 0 && <span className="wshSub">{h.alumnosVencidos} {h.alumnosVencidos === 1 ? "alumno" : "alumnos"}</span>}
														</td>
														{celda(m, h.porCobrar)}
														<td><Avance cobrado={colegiaturaDe(h)} vencido={h.vencido} porCobrar={h.porCobrar} /></td>
													</tr>
													{abierto && (
														<tr className="wshDetalle">
															<td colSpan={columnas}>
																{lista === "cargando" && <div className="wshTenue wshDetalleNota">Cargando alumnos…</div>}
																{lista === "error" && <div className="wshAviso">No se pudieron cargar los alumnos.</div>}
																{Array.isArray(lista) && (
																	<div className="wshDetalleCaja">
																		<div className="wshDetalleCabeza">
																			<span className="wshDetalleTitulo">{horarioBonito(h.nombre)}</span>
																			<span className="wshTenue">Mensualidad {dineroTexto(m, h.mensualidad, 2)} · {h.meses} {h.meses === 1 ? "mes" : "meses"}</span>
																			<button type="button" className="wshBoton wshBotonChico" onClick={(e) => { e.stopPropagation(); descargarAlumnos(h, lista); }}>Descargar</button>
																			{lista.some((a) => a.vencido > 0.5) && (
																				<button type="button" className="wshBoton wshBotonChico" onClick={(e) => {
																					e.stopPropagation();
																					setWaEnvio({ ids: Array.from(new Set(lista.filter((a) => a.vencido > 0.5).map((a) => a.alumnoId))), plantel: Number(h.plantel), ref: h.nombre });
																				}}>WhatsApp a los que Deben ({new Set(lista.filter((a) => a.vencido > 0.5).map((a) => a.alumnoId)).size})</button>
																			)}
																		</div>
																		<div className="wshTablaCaja">
																			<table className="wshTablaAlumnos">
																				<thead>
																					<tr>
																						<th>Alumno</th>
																						<th>Matrícula</th>
																						<th className="wshNum">Meses pagados</th>
																						<th className="wshNum">Mensualidades</th>
																						<th className="wshNum">Insc. y recargos</th>
																						<th className="wshNum">Kits</th>
																						<th className="wshNum">Productos</th>
																						<th className="wshNum">Servicios</th>
																						<th className="wshNum">{conRango ? "Total en rango" : "Total"}</th>
																						<th className="wshNum">Vencido</th>
																						<th className="wshNum">Por cobrar</th>
																						<th>Último pago</th>
																					</tr>
																				</thead>
																				<tbody>
																					{lista.length === 0 && <tr><td colSpan={12} className="wshVacio">Sin alumnos</td></tr>}
																					{lista.map((a) => (
																						<tr key={a.matricula} className={a.activa ? "" : "wshInactiva"}>
																							<td>
																								<a className="wshAlumno" href={`/statement?alumno=${a.alumnoId}&plantel=${h.plantel}`} target="_blank" rel="noreferrer"
																									onClick={(e) => e.stopPropagation()}>{a.nombre}</a>
																								<span className="wshCorrelativo">{a.correlativo}</span>
																								<span onClick={(e) => e.stopPropagation()}><BotonWhatsappAlumno campusId={Number(h.plantel)} studentId={a.alumnoId} nombre={a.nombre} className="wshWa" /></span>
																							</td>
																							<td className="wshNowrap">{a.estado}</td>
																							<td className="wshNum">{a.cuotasPagadas}<span className="wshDe"> de {a.cuotas}</span></td>
																							{celda(m, a.cobradoMens, "", 2)}
																							{celda(m, a.cobradoInsc + a.cobradoRec, "", 2)}
																							{celda(m, a.cobradoKits, "", 2)}
																							{celda(m, a.cobradoProd, "", 2)}
																							{celda(m, a.cobradoServ, "", 2)}
																							{celda(m, cobradoDe(a), "wshTotal", 2)}
																							<td className="wshNum">
																								{a.vencido > 0.5 ? <span className="wshRojo">{dineroTexto(m, a.vencido, 2)}</span> : <span className="wshTenue">–</span>}
																								{a.vencidoDesde && a.vencido > 0.5 && <span className="wshSub">desde {fechaCorta(a.vencidoDesde)}</span>}
																							</td>
																							{celda(m, a.porCobrar, "", 2)}
																							<td className="wshNowrap">{fechaCorta(a.ultimoPago)}</td>
																						</tr>
																					))}
																				</tbody>
																			</table>
																		</div>
																	</div>
																)}
															</td>
														</tr>
													)}
												</React.Fragment>
											);
										})}
									</React.Fragment>
								);
							})}
						</tbody>
						{filtrados.length > 1 && (
							<tfoot>
								<tr>
									<td colSpan={lead}>Total</td>
									<td className="wshNum">{cifra(totales.activos)}</td>
									<td className="wshNum"><Montos valor={totales.mens} /></td>
									<td className="wshNum"><Montos valor={totales.otros} /></td>
									<td className="wshNum"><Montos valor={totales.kits} /></td>
									<td className="wshNum"><Montos valor={pieProd.prod} /></td>
									<td className="wshNum"><Montos valor={pieProd.serv} /></td>
									<td className="wshNum wshTotal"><Montos valor={totales.cobrado} /></td>
									<td className="wshNum"><Montos valor={totales.vencido} clase="wshRojo" /></td>
									<td className="wshNum"><Montos valor={totales.porCobrar} /></td>
									<td />
								</tr>
							</tfoot>
						)}
					</table>
				</div>
			</div>
			{waEnvio && <EnvioMasivo campusId={waEnvio.plantel} studentIds={waEnvio.ids} origen="saldos" referencia={waEnvio.ref} onClose={() => setWaEnvio(null)} />}
		</div>
	);
};

export default ScheduleBalancesController;
